// frontend/src/components/MembersTable.jsx

import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// Utilidades de fecha
import { calculateAge } from '../utils/dateUtils'
import { toTitleCase } from '../utils/formatters'

// Traducción
import { useTranslation } from '../i18n'

// Lista de países
import COUNTRY_LIST from '../config/countries.json'

export default function MembersTable(
  { members,
    session,
    onEdit,
    onDelete,
    isReadOnly,
    isLeader
  }) {

  const { _ } = useTranslation()

  const isLeaderByRoleOrContext = session.role === 'leader'
    || !!session.parentHash;

  const formatName = (m) => {
    const secondInitial = m.middleName ? m.middleName[0] + '.' : ''
    const fullName = `${m.firstName} ${secondInitial} ${m.lastName}`
    return toTitleCase(fullName)
  }

  const formatGender = (g) => {
    if (!g) return '—'
    return g === 'M' ? _('Male') : g === 'F' ? _('Female') : g
  }

  const getCountryName = (code) => {
    if (!code) return '—'
    const match = COUNTRY_LIST.find(
      (c) => c.code.toUpperCase() === code.toUpperCase()
    )
    return match ? toTitleCase(match.name) : code
  }

  return (
    <div className="overflow-x-auto">
      <table className="table-default hidden md:table">
        <thead>
          <tr className="table-head-row">
            <th>{_('Name')}</th>
            <th>{_('Age')}</th>
            <th>{_('Gender')}</th>
            <th>{_('Nationality')}</th>
            <th className="!text-center">{_('Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {Array.isArray(members) && members.map((m) => (
            <tr key={m.personId || m.tempId || `temp-${Date.now()}`} className="table-row">
              <td>
                {formatName(m)}
                {((m.validation_source &&
                  m.validation_source.startsWith('PADRON_')) ||
                  m.validation_status === 'VERIFIED') && (
                    <span
                      className="ml-1 text-amber-600 dark:text-yellow-400"
                      title="Verificado"
                      aria-label="Miembro verificado"
                    >
                      ★
                    </span>
                  )}
              </td>
              <td>{calculateAge(m.birthdate)}</td>
              <td>{formatGender(m.gender)}</td>
              <td>{getCountryName(m.nationality)}</td>
              <td className="flex flex-row gap-1 items-center justify-center">
                {m.canEdit && (!isReadOnly || isLeader) ? (
                  <>
                    <button
                      onClick={() => onEdit(m)}
                      className="icon-button text-blue-600"
                      title={_('Edit')}
                    >
                      <FontAwesomeIcon icon="pencil-alt" />
                    </button>

                    <button
                      onClick={() => onDelete?.(m)}
                      className="icon-button text-red-600"
                      title={_('Delete')}
                    >
                      <FontAwesomeIcon icon="trash" />
                    </button>
                  </>
                ) : (
                  <span className="text-muted text-[11px] shrink-0">
                    {_('No actions')}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Vista para Móviles (compacta) */}
      <div className="space-y-3 md:hidden">
        {Array.isArray(members) && members.map((m) => {

          const g = String(m.gender || '').toLowerCase()
          const genderClasses = g.includes('male')
            ? 'border-blue-300 text-blue-700 bg-blue-50 dark:border-blue-700 ' +
            'dark:text-blue-300 dark:bg-blue-950/40'
            : g.includes('female')
              ? 'border-pink-300 text-pink-700 bg-pink-50 dark:border-pink-700 ' +
              'dark:text-pink-300 dark:bg-pink-950/40'
              : 'border-slate-300 text-slate-600 bg-slate-50 dark:border-slate-600 ' +
              'dark:text-slate-300 dark:bg-slate-900/40';
          const verified =
            (m.validation_source && m.validation_source.startsWith('PADRON_')) ||
            m.validation_status === 'VERIFIED'
          const key = m.personId || m.tempId || m.hash || `temp-${Date.now()}`

          return (
            <div key={key} className="card px-3 py-2">
              {/* Encabezado: Nombre + acciones */}
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <span className="font-semibold truncate">
                      {formatName(m)}
                    </span>
                    {verified && (
                      <span
                        className="ml-1 text-amber-600 dark:text-yellow-400"
                        title={_('Verified')}
                        aria-label={_('Verified')}
                      >
                        ★
                      </span>
                    )}
                  </div>
                </div>

                {m.canEdit && (!isReadOnly || isLeader) ? (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => onEdit(m)}
                      className="icon-button text-blue-600"
                      title={_('Edit')}
                    >
                      <FontAwesomeIcon icon="pencil-alt" />
                    </button>
                    <button
                      onClick={() => onDelete?.(m)}
                      className="icon-button text-red-600"
                      title={_('Delete')}
                    >
                      <FontAwesomeIcon icon="trash" />
                    </button>
                  </div>
                ) : (
                  <span className="text-muted text-[11px] shrink-0">
                    {_('No actions')}
                  </span>
                )}
              </div>

              {/* Metadatos (chips) — SOLO MÓVIL */}
              <div className="mt-1 flex flex-wrap gap-1.5 text-xs text-muted md:hidden">
                <span className="sr-only">{_('Age')}:</span>
                <span
                  className="px-2 py-0.5 rounded-full border border-gray-200
                            text-gray-700 bg-gray-50 dark:border-gray-700
                            dark:text-gray-300 dark:bg-gray-800"
                >
                  {calculateAge(m.birthdate)} {_('yrs')}
                </span>

                <span className="sr-only">{_('Gender')}:</span>
                <span className={`px-2 py-0.5 rounded-full border ${genderClasses}`}>
                  {formatGender(m.gender)}
                </span>

                <span className="sr-only">{_('Nationality')}:</span>
                <span
                  className="px-2 py-0.5 rounded-full border border-slate-200
                            text-slate-600 bg-slate-50 dark:border-slate-700
                            dark:text-slate-300 dark:bg-slate-900/40 truncate
                            max-w-[60%]"
                >
                  {getCountryName(m.nationality)}
                </span>
              </div>

            </div>
          )
        })}
      </div>


    </div>
  )
}
